import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Receipt, Wallet, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI, expensesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ModernButton, ModernCard, ModernInput, ModernLoadingSpinner, ModernModal, ModernSelect, ModernStatCard, ModernTable } from '../components/ui/ModernComponents';
import { formatCurrency } from '../utils/currency';
import { formatDateOnlyFr, toDateInputValue } from '../utils/dateFormat';
import { EXPENSE_CATEGORIES, EXPENSE_PAYMENT_METHODS, expenseCategoryLabel, expensePaymentLabel, todayInCasablanca } from '../constants/expenses';

const emptyForm = storeId => ({ label: '', category: 'autre', amount: '', expense_date: todayInCasablanca(), payment_method: 'espece', note: '', store_id: storeId || '' });
const apiError = error => error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.';

export default function Depenses() {
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({ start_date: searchParams.get('start_date') || '', end_date: searchParams.get('end_date') || '', category: '', store_id: '' }));
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], summary: { count: 0, total_amount: 0, all_time_total: 0 }, pageSize: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stores, setStores] = useState([]);
  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(user?.store_id));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    try {
      const response = await expensesAPI.list({ ...filters, page });
      if (id === requestId.current) {
        if (page > 1 && response.data.rows.length === 0) setPage(p => p - 1);
        else setData(response.data);
      }
    } catch (e) {
      if (id === requestId.current) setError(apiError(e));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [filters, page]);
  useEffect(() => { load(); return () => { requestId.current += 1; }; }, [load]);
  useEffect(() => {
    if (isAdmin) authAPI.getStores().then(res => setStores(res.data)).catch(() => toast.error('Impossible de charger les magasins.'));
  }, [isAdmin]);

  const changeFilter = (key, value) => { setPage(1); setFilters(f => ({ ...f, [key]: value })); };
  const openForm = row => {
    setEditingId(row?.id || null);
    setForm(row ? { ...row, expense_date: toDateInputValue(row.expense_date) } : emptyForm(user?.store_id));
    setFormError('');
    setModal(true);
  };
  const save = async event => {
    event.preventDefault();
    if (saving) return;
    setFormError('');
    if (!form.store_id) { setFormError('Veuillez sélectionner un magasin.'); return; }
    setSaving(true);
    try {
      const payload = { label: form.label.trim(), category: form.category, amount: String(form.amount).replace(',', '.'), expense_date: form.expense_date, payment_method: form.payment_method, note: form.note, store_id: Number(form.store_id) };
      if (editingId) await expensesAPI.update(editingId, payload);
      else await expensesAPI.create(payload);
      toast.success(editingId ? 'Dépense modifiée' : 'Dépense ajoutée');
      setModal(false);
      await load();
    } catch (e) { setFormError(apiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async row => {
    if (!window.confirm(`Supprimer la dépense « ${row.label} » de ${formatCurrency(row.amount)} ?`)) return;
    setDeletingId(row.id);
    try { await expensesAPI.remove(row.id); toast.success('Dépense supprimée'); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setDeletingId(null); }
  };
  const columns = [
    { key: 'expense_date', label: 'Date', render: value => formatDateOnlyFr(value) },
    { key: 'label', label: 'Dépense', render: (value, row) => <div><span className="font-medium">{value}</span>{row.note && <p className="text-xs text-neutral-500 whitespace-pre-wrap max-w-xs">{row.note}</p>}</div> },
    { key: 'category', label: 'Catégorie', render: expenseCategoryLabel },
    { key: 'amount', label: 'Montant', render: value => <span className="font-semibold tabular-nums">{formatCurrency(value)}</span> },
    { key: 'payment_method', label: 'Mode', render: expensePaymentLabel },
    ...(isAdmin ? [{ key: 'store_name', label: 'Magasin' }] : []),
    { key: 'created_by_name', label: 'Ajoutée par' },
    ...(canManage ? [{ key: 'actions', label: 'Actions', render: (_, row) => <div className="flex gap-2">
      <ModernButton variant="secondary" size="sm" onClick={() => openForm(row)} aria-label={`Modifier ${row.label}`}><Pencil className="w-4 h-4" /></ModernButton>
      <ModernButton variant="danger" size="sm" disabled={deletingId !== null} onClick={() => remove(row)} aria-label={`Supprimer ${row.label}`}><Trash2 className="w-4 h-4" /></ModernButton>
    </div> }] : []),
  ];
  return <div className="py-6 space-y-6">
    <div className="flex flex-wrap justify-between items-center gap-4">
      <div><h1 className="text-2xl font-bold text-neutral-900">Dépenses</h1><p className="text-neutral-500">Enregistrez les dépenses réglées de votre magasin.</p></div>
      <div className="flex items-center gap-3"><Link className="text-primary-600 hover:underline" to="/">Tableau de bord</Link><ModernButton onClick={() => openForm()}><Plus className="w-4 h-4 mr-2" />Ajouter une dépense</ModernButton></div>
    </div>
    <ModernCard><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <ModernInput label="Du" type="date" value={filters.start_date} onChange={e => changeFilter('start_date', e.target.value)} />
      <ModernInput label="Au" type="date" value={filters.end_date} onChange={e => changeFilter('end_date', e.target.value)} />
      <ModernSelect label="Catégorie" value={filters.category || null} options={EXPENSE_CATEGORIES} isClearable placeholder="Toutes les catégories" onChange={option => changeFilter('category', option?.value || '')} />
      {isAdmin && <ModernSelect label="Magasin" value={filters.store_id || null} options={stores} isClearable placeholder="Tous les magasins" onChange={option => changeFilter('store_id', option?.value || '')} />}
      <ModernButton variant="secondary" onClick={() => { setPage(1); setFilters({ start_date: '', end_date: '', category: '', store_id: '' }); }}>Tout afficher</ModernButton>
    </div></ModernCard>
    {error ? <ModernCard><p role="alert" className="text-red-600">{error}</p><ModernButton onClick={load}>Réessayer</ModernButton></ModernCard> : loading ? <div className="flex justify-center py-12"><ModernLoadingSpinner /></div> : <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ModernStatCard title="Dépenses de la période" value={formatCurrency(data.summary.total_amount)} subtitle="Total des résultats filtrés, toutes pages" icon={<Wallet />} variant="danger" />
        <ModernStatCard title="Total dépenses — toutes dates" value={formatCurrency(data.summary.all_time_total)} subtitle="Même magasin et catégorie, sans filtre de date" icon={<Receipt />} variant="warning" />
        <ModernStatCard title="Nombre de dépenses" value={data.summary.count} subtitle="Période et filtres sélectionnés" icon={<Calendar />} />
      </div>
      <ModernCard>{data.rows.length ? <ModernTable columns={columns} data={data.rows} /> : <p className="text-center text-neutral-500 py-8">Aucune dépense pour ces filtres.</p>}
        <div className="flex justify-between items-center gap-3 mt-4"><span className="text-sm text-neutral-500">Page {page} sur {Math.max(1, Math.ceil(Number(data.summary.count) / data.pageSize))}</span><div className="flex gap-2"><ModernButton variant="secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Précédent</ModernButton><ModernButton variant="secondary" disabled={page * data.pageSize >= Number(data.summary.count)} onClick={() => setPage(p => p + 1)}>Suivant</ModernButton></div></div>
      </ModernCard>
    </>}
    <ModernModal isOpen={modal} onClose={() => { if (!saving) setModal(false); }} title={editingId ? 'Modifier la dépense' : 'Ajouter une dépense'} size="lg">
      <form onSubmit={save} className="space-y-4"><fieldset disabled={saving} className="space-y-4">
        {formError && <p role="alert" className="text-red-600">{formError}</p>}
        <ModernInput label="Libellé" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required maxLength={200} placeholder="Ex. Loyer septembre" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ModernInput label="Montant (MAD)" type="number" min="0.01" max="9999999999.99" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required />
          <ModernInput label="Date de la dépense" type="date" value={form.expense_date} onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))} required />
          <ModernSelect label="Catégorie" value={form.category} options={EXPENSE_CATEGORIES} isDisabled={saving} onChange={option => setForm(f => ({ ...f, category: option.value }))} />
          <ModernSelect label="Mode de paiement" value={form.payment_method} options={EXPENSE_PAYMENT_METHODS} isDisabled={saving} onChange={option => setForm(f => ({ ...f, payment_method: option.value }))} />
          {isAdmin && <ModernSelect label="Magasin" value={form.store_id || null} options={stores} isDisabled={saving} onChange={option => setForm(f => ({ ...f, store_id: option.value }))} />}
        </div>
        <ModernInput label="Note (facultatif)" value={form.note || ''} maxLength={2000} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
        <p className="text-sm text-neutral-500">Cette dépense sera déduite des paiements enregistrés dans le solde de la période du tableau de bord.</p>
        <div className="flex justify-end gap-3"><ModernButton type="button" variant="secondary" onClick={() => setModal(false)} disabled={saving}>Annuler</ModernButton><ModernButton type="submit" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</ModernButton></div>
      </fieldset></form>
    </ModernModal>
  </div>;
}
